import { firstAllowedMenuPath, toLoginLandingCandidates } from './admin-menu-structure.ts';
import { ADMIN_ROUTE_PERMISSIONS, isMenuItemVisible, type MenuPermission } from './menu-access.ts';
import { MOBILE_CREDITS_PERMISSIONS } from '../pages/credits/credits-tabs.ts';

export const MOBILE_MEDIA_QUERY = '(max-width: 767px)';
export const MOBILE_UNAVAILABLE_PATH = '/mobile-unavailable';

type MobilePage = {
  path: string;
  label: string;
  permission: MenuPermission;
  parent?: '/users' | '/organizations';
};

/** 手机能力范围。导航和深链共用此表，详情仍使用独立权限。 */
const MOBILE_PAGES: readonly MobilePage[] = [
  { path: '/', label: '概览', permission: ADMIN_ROUTE_PERMISSIONS.dashboard },
  { path: '/users', label: '用户', permission: ADMIN_ROUTE_PERMISSIONS.users },
  { path: '/organizations', label: '组织', permission: ADMIN_ROUTE_PERMISSIONS.organizations },
  { path: '/credits', label: '算力', permission: MOBILE_CREDITS_PERMISSIONS },
  {
    path: '/users/:userId',
    label: '用户详情',
    permission: ADMIN_ROUTE_PERMISSIONS.userDetail,
    parent: '/users',
  },
  {
    path: '/organizations/:orgId',
    label: '组织详情',
    permission: ADMIN_ROUTE_PERMISSIONS.organizationDetail,
    parent: '/organizations',
  },
];

export function getMobilePage(pathname: string): MobilePage | undefined {
  const path = pathname.replace(/\/$/, '') || '/';
  return MOBILE_PAGES.find((page) =>
    page.parent ? new RegExp(`^${page.parent}/[^/]+$`).test(path) : page.path === path
  );
}

export function getMobileNavigation(permissions: readonly string[]): MobilePage[] {
  return MOBILE_PAGES.filter(
    (page) => !page.parent && isMenuItemVisible(page.permission, permissions)
  );
}

export function getAdminLandingPath(permissions: readonly string[], mobile: boolean): string {
  return mobile
    ? (getMobileNavigation(permissions)[0]?.path ?? MOBILE_UNAVAILABLE_PATH)
    : firstAllowedMenuPath(permissions);
}

function parseLocalTarget(target: string | null | undefined): URL | null {
  if (
    !target ||
    !target.startsWith('/') ||
    target.startsWith('//') ||
    target.includes('\\') ||
    Array.from(target).some((character) => character.charCodeAt(0) < 32)
  )
    return null;
  try {
    const url = new URL(target, 'https://admin.invalid');
    return url.origin === 'https://admin.invalid' ? url : null;
  } catch {
    return null;
  }
}

export function resolveLoginTarget(
  target: string | null | undefined,
  permissions: readonly string[],
  mobile: boolean
): string {
  const url = parseLocalTarget(target);
  if (url) {
    const mobilePage = getMobilePage(url.pathname);
    const permission = mobile
      ? mobilePage?.permission
      : (toLoginLandingCandidates().find((page) => page.key === url.pathname)?.permission ??
        mobilePage?.permission);
    if (permission && isMenuItemVisible(permission, permissions))
      return `${url.pathname}${url.search}${url.hash}`;
  }
  return getAdminLandingPath(permissions, mobile);
}

export function getListReturnPath(
  target: string | null | undefined,
  list: '/users' | '/organizations'
): string {
  const url = parseLocalTarget(target);
  return url?.pathname === list ? `${url.pathname}${url.search}` : list;
}
